import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { CheckCircle, Loader2, XCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

export const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [status, setStatus] = useState('checking');
  const [attempts, setAttempts] = useState(0);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId && token) {
      pollPaymentStatus();
    }
  }, [sessionId, token]);

  const pollPaymentStatus = async () => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await axios.get(`${API}/api/checkout.php?action=status&session_id=${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.payment_status === 'paid') {
        setStatus('success');
        return;
      } else if (response.data.status === 'expired') {
        setStatus('expired');
        return;
      }

      // Continue polling
      setAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      <main className="pt-32 pb-16 px-6" data-testid="payment-success-page">
        <div className="max-w-md mx-auto text-center">
          {status === 'checking' && (
            <div className="animate-fadeIn">
              <Loader2 className="w-16 h-16 text-primary mx-auto mb-6 animate-spin" />
              <h1 className="text-3xl font-heading font-bold mb-4">
                Zahlung wird überprüft...
              </h1>
              <p className="text-muted-foreground">
                Bitte warten Sie einen Moment.
              </p>
            </div>
          )}

          {status === 'success' && (
            <div className="animate-fadeIn">
              <CheckCircle className="w-20 h-20 text-green-500 mx-auto mb-6" />
              <h1 className="text-3xl font-heading font-bold mb-4">
                Zahlung <span className="text-green-500">erfolgreich!</span>
              </h1>
              <p className="text-muted-foreground mb-8">
                Vielen Dank für Ihre Buchung. Sie erhalten in Kürze eine Bestätigung per E-Mail.
              </p>
              <div className="flex flex-col gap-4">
                <Button onClick={() => navigate('/dashboard')} className="btn-primary">
                  Zu meinen Buchungen
                </Button>
                <Button variant="outline" onClick={() => navigate('/courses')}>
                  Weitere Kurse entdecken
                </Button>
              </div>
            </div>
          )}

          {(status === 'error' || status === 'expired' || status === 'timeout') && (
            <div className="animate-fadeIn">
              <XCircle className="w-20 h-20 text-destructive mx-auto mb-6" />
              <h1 className="text-3xl font-heading font-bold mb-4">
                {status === 'expired' ? 'Sitzung abgelaufen' : 'Etwas ist schiefgelaufen'}
              </h1>
              <p className="text-muted-foreground mb-8">
                {status === 'expired' 
                  ? 'Ihre Zahlungssitzung ist abgelaufen. Bitte versuchen Sie es erneut.'
                  : 'Bei der Überprüfung Ihrer Zahlung ist ein Fehler aufgetreten.'}
              </p>
              <div className="flex flex-col gap-4">
                <Button onClick={() => navigate('/courses')} className="btn-primary">
                  Zurück zu den Kursen
                </Button>
                <Button variant="outline" onClick={() => navigate('/dashboard')}>
                  Mein Bereich
                </Button>
              </div>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};
